/**
 * Shared image-upload prep: compress the full image and, for E2EE uploads,
 * also produce a small client-encrypted thumbnail. The server can't resize
 * ciphertext, so without this every encrypted list thumbnail would download
 * the full-resolution blob. Mirrors the iOS prepareImageUpload path.
 */
import { compressImage } from './compress';
import { bytesToBlob } from './crypto';

/** Thumbnail target: width+height ≤ 700 (~400px long edge), lower quality. */
export const THUMB_MAX_DIM_SUM = 700;
const THUMB_QUALITY = 0.6;

export interface PreparedImageUpload {
  /** Encrypted-or-plaintext full image, ready for the `file` form field. */
  fileBlob: Blob;
  /** Encrypted thumbnail for the `thumbnail` field — null unless E2EE. */
  thumbBlob: Blob | null;
  mime: string;
  width: number;
  height: number;
  encrypted: boolean;
}

export async function prepareImageUpload(
  file: File,
  encryptForUpload: (bytes: Uint8Array) => { bytes: Uint8Array; encrypted: boolean },
): Promise<PreparedImageUpload> {
  const full = await compressImage(file);
  const fullEnc = encryptForUpload(full.bytes);

  // Only E2EE images need a client thumbnail; the server resizes plaintext on
  // demand, so sending one for unencrypted uploads would just waste bandwidth.
  let thumbBlob: Blob | null = null;
  if (fullEnc.encrypted) {
    const thumb = await compressImage(file, THUMB_MAX_DIM_SUM, THUMB_QUALITY);
    const thumbEnc = encryptForUpload(thumb.bytes);
    thumbBlob = bytesToBlob(thumbEnc.bytes, 'application/octet-stream');
  }

  return {
    fileBlob: bytesToBlob(fullEnc.bytes, 'application/octet-stream'),
    thumbBlob,
    mime: full.mime,
    width: full.width,
    height: full.height,
    encrypted: fullEnc.encrypted,
  };
}
